import { ExternalLink, FileText } from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/badge';
import type { DiscriminatedPoliceReportCreatedEvent } from '@/services/api/generated/taskActivityService';

import {
  EventActions,
  EventBody,
  EventContainer,
  EventHeader,
} from './EventPrimitives';

interface PoliceReportCreatedEventProps {
  event: DiscriminatedPoliceReportCreatedEvent;
  matterId: string;
}

export const PoliceReportCreatedEvent: React.FC<
  PoliceReportCreatedEventProps
> = ({ event, matterId }) => {
  const { payload, occurred_at, user, agent } = event;
  const { police_report, needs_approval } = payload;

  // Get department name
  const departmentName =
    police_report.police_department?.name || 'Unknown Department';

  // Get attestation status
  const attestationStatus = police_report.attestation_status
    ? formatAttestationStatus(police_report.attestation_status)
    : null;

  // Get report identifier
  const reportIdentifier = police_report.report_identifier;

  return (
    <EventContainer>
      <EventHeader
        icon={<FileText size={20} />}
        title="Police Report Created"
        timestamp={occurred_at}
        actor={user}
        agent={agent}
      />

      <EventBody>
        <div className="space-y-1.5">
          {/* Department Name */}
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="font-medium">{departmentName}</span>
            {attestationStatus && (
              <>
                <span className="text-slate-400">•</span>
                <Badge variant="outline">{attestationStatus}</Badge>
              </>
            )}
            {needs_approval && (
              <>
                <span className="text-slate-400">•</span>
                <Badge variant="outline">Needs Approval</Badge>
              </>
            )}
          </div>

          {/* Report Identifier */}
          {reportIdentifier && (
            <div className="text-xs">
              <span className="text-slate-600">Report #:</span>{' '}
              <span className="font-mono text-[11px]">{reportIdentifier}</span>
            </div>
          )}

          {/* Reported Location */}
          {police_report.reported_location && (
            <div className="text-xs text-slate-600">
              {police_report.reported_location}
            </div>
          )}

          {/* Attestation Description */}
          {police_report.attestation_description && (
            <div className="text-xs text-slate-600 italic">
              {police_report.attestation_description}
            </div>
          )}
        </div>
      </EventBody>

      <EventActions>
        <a
          href={`/matters/${matterId}/police-reports/${police_report.id}`}
          className="text-[11px] text-blue-600 hover:text-blue-800 flex items-center gap-1 hover:underline"
        >
          View Report
          <ExternalLink size={11} />
        </a>
      </EventActions>
    </EventContainer>
  );
};

// Helper function
function formatAttestationStatus(status: string): string {
  const statuses: Record<string, string> = {
    rejected_verified: 'Rejected (Verified)',
    unclear: 'Unclear',
    inferred: 'Inferred',
    attested_unverified: 'Attested (Unverified)',
    attested_verified: 'Attested (Verified)',
    verified_received: 'Received',
  };
  return statuses[status] || status;
}
